// Angular Imports
// =========================================================
import {
  Component,
  Input,
  type OnDestroy,
  type OnInit,
  Output,
  EventEmitter,
  ViewChild,
  HostListener
} from '@angular/core'
import { Subject, type Subscription } from 'rxjs'
// AG Grid Imports
// =========================================================
import type {
  GetDetailRowDataParams,
  GetRowIdParams,
  IDetailCellRendererParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  SelectionChangedEvent,
  GetContextMenuItemsParams,
  ICellRendererParams,
  DetailGridInfo,
  RowSelectedEvent,
  RowGroupOpenedEvent,
  RowClassParams,
  DisplayedColumnsChangedEvent,
  GridColumnsChangedEvent,
  ColumnMovedEvent,
  FilterChangedEvent,
  SortChangedEvent
} from 'ag-grid-community'

// Angular Material Imports
// =========================================================
import { MatMenuTrigger } from '@angular/material/menu'
// Prime NG Imports
// =========================================================
import { DynamicDialogRef, DialogService } from 'primeng/dynamicdialog'
// Custom Imports
// =========================================================
import { RO_SharedGridOptions, TreeDataGridOptions } from './ro-grid-options'
import {
  AutoSizeColumns,
  CustomGridOptions,
  DefaultGridContextMenu
} from 'src/app/05_ag-grid-configs/02_global-settings/grid-options'
import { FormatKey, ReverseFormatKey } from 'src/app/utils/global_functions'
import {
  GenerateCols,
  GenerateDynamicCols,
  OM_GenerateGridData
} from 'src/app/05_ag-grid-configs/04_generate-colDefs/review-orders-grid-config'
import {
  DefaultUserInputLinesGrid,
  UserInputLinesGridInterface
} from 'src/app/core/interfaces/review-orders-interface'
import { UserInputConfigSettingsComponent } from 'src/app/04_modals/review-orders/user-input-config/user-input-config.component'
import { PackSummaryConfigComponent } from 'src/app/04_modals/review-orders/pack-summary-config/pack-summary-config.component'
import { MismatchChartComponent } from 'src/app/04_modals/review-orders/mismatch-chart/mismatch-chart.component'
import { OhOoModalComponent } from 'src/app/04_modals/review-orders/oh-oo-modal/oh-oo-modal.component'
import { StoreTotalsChartsComponent } from 'src/app/04_modals/review-orders/store-totals-charts/store-totals-charts.component'
import { ReviewOrdersAPIs } from 'src/app/core/apis/review-orders_api-calls'
import { ConfirmPopupComponent } from 'src/app/03_shared-components/01_alerts/confirm-popup/confirm-popup.component'
import { TriggerApiCallsService } from 'src/app/core/services/cancel-api-call'
import { GeneralAPIs } from 'src/app/core/apis/general_api-calls'
import { DefaultChartOptions } from 'src/app/utils/global-chart-settings'
import {
  SaveGridState,
  SaveGridStateWithData
} from 'src/app/05_ag-grid-configs/02_global-settings/grid-functions-general'
import { cloneDeep } from 'lodash'
import { NavigationStart, Router } from '@angular/router'
import { SizeDistroDefaultParametersAPIs } from 'src/app/core/apis/size-distro-default-parameters_api-calls'
import { ReviewOrdersUserInputToolPanelComponent } from 'src/app/05_ag-grid-configs/03_custom-components/custom-tool-panels/review-orders-user-input-tool-panel/review-orders-user-input-tool-panel.component'
import { SideBarPanels } from 'src/app/05_ag-grid-configs/02_global-settings/grid-variables'
import { type TreeNode } from 'primeng/api'

interface PinnedTopRowNodeInterface {
  topRow: any
  isExpanded: boolean
  children: any[]
}

@Component({
  standalone: false,
  selector: 'app-ro-ag-grid',
  templateUrl: './ro-ag-grid.component.html',
  styleUrls: ['./ro-ag-grid.component.scss']
})
export class RoAgGridComponent implements OnInit, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>()
  private readonly routerSubscription: Subscription
  private readonly sideBarPanels: any = SideBarPanels()
  detailCellRendererParams: any

  // Advanced Filtering (Product Filters) -- only meaningful on the
  // 'user_input' tab's Job List grid, mirroring Size Distro Summary's
  // productFilters/productTreeData pattern.
  productFilters: any = []
  productTreeData: TreeNode[] = []

  // Ag Grid Configuration
  private readonly customGridOptions: any = {
    ...CustomGridOptions,
    context: {
      componentParent: this,
      pageTitle: 'Review orders - user input',
      allowRowGroupToggle: true,
      externalFilters: ['productFilters'],
      viewCharts: params => this.viewTotalsCharts(params),
      openMismatchChartModal: params => this.openMismatchChartModal(params),
      openPackConfigModal: params => this.openPackConfigModal(params),
      openUncommittedOhOoModal: params => this.openUncommittedOhOoModal(params),
      openDeleteConfirmDialog: event => {
        console.log('delete confirm event', event)
        this.confirmPopupDelete.confirm(event, 'Delete Buy Group')
      }
    }
  }

  outputGridOptions: any = {
    ...CustomGridOptions,
    // rowSelection: {
    //   mode: 'singleRow'
    // },
    pagination: false,
    // getRowId: (params: GetRowIdParams) => {
    //   return params.data.detail_id
    // },
    getContextMenuItems: params =>
      params?.context?.gridName === 'user_input_detail'
        ? []
        : [
            {
              name: 'Save Grid View',
              action: async () => {
                await SaveGridState(this.gridApi, params, params?.context?.gridName, true)
              },
              cssClasses: ['text-accent']
            },
            'separator',
            ...DefaultGridContextMenu(params)
          ],
    context: {
      componentParent: this,
      allowRowGroupToggle: true,
      openTargetBuyChart: params => this.openTargetBuyChart(params),
      openCommittedOhOoModal: params => this.openCommittedOhOoModal(params),
      openUncommittedOhOoModal: params => this.openUncommittedOhOoModal(params),
      getPackConfigs: params => this.packConfigs,
      onExpandCollapsePinnedRow: (params, expand) => {
        params.isExpanded = expand
        switch (params.carton_id) {
          case 'SKU Totals by Size':
            this.pinnedTopRowDataSections.totals.isExpanded = expand
            this.pinnedTopRowDataSections.totals.topRow.isExpanded = expand
            break
          case 'SKU % by Size':
            this.pinnedTopRowDataSections.totalsPct.isExpanded = expand
            this.pinnedTopRowDataSections.totalsPct.topRow.isExpanded = expand
            break
          case 'In Bulk':
            this.pinnedTopRowDataSections.bulkTotals.isExpanded = expand
            this.pinnedTopRowDataSections.bulkTotals.topRow.isExpanded = expand
            break
          case 'In Pack':
            this.pinnedTopRowDataSections.packTotals.isExpanded = expand
            this.pinnedTopRowDataSections.packTotals.topRow.isExpanded = expand
            break
          case 'Profiles':
            this.pinnedTopRowDataSections.profileRows.isExpanded = expand
            this.pinnedTopRowDataSections.profileRows.topRow.isExpanded = expand
            break
          default:
            break
                    // // styc row - expand to distro id
                    // const child = this.pinnedTopRowDataSections.totalsPct.children.find(child => child.styc_id == params.styc_id)
                    // child.isExpanded = expand
                    // // child.topRow.isExpanded = expand
        }
        this.updateCartonSummaryPinnedRowExpansion()
      }
    }
  }

  pinnedTopRowDataSections: {
    totals: PinnedTopRowNodeInterface
    totalsPct: PinnedTopRowNodeInterface
    bulkTotals: PinnedTopRowNodeInterface
    packTotals: PinnedTopRowNodeInterface
    profileRows: PinnedTopRowNodeInterface
  } = {
      totals: null,
      totalsPct: null,
      bulkTotals: null,
      packTotals: null,
      profileRows: null
    }

  pinnedTopRowData = []

  bulkTotalsForChart = {}
  packTotalsForChart = {}

  @Input() packConfigs = null

  outputChartData = null
  outputDetailChartData = null
  outputChartOptions = null
  outputDetailChartOptions = null

  // Basic Input Variables
  @Input('importTabData') importTabData: any
  @Input() selectableStycIds: string[] = []
  @Input() selectedStycId: string
  @Input() canViewData: boolean = true
  @Input() selectedRowIndex: number = 0
  // Dynamic Inputs with select functions -----------
  inputLinesSelectedRows: UserInputLinesGridInterface = {
    ...DefaultUserInputLinesGrid
  }

  // App sync
  subscription: any = null

  @Input('inputLinesSelectedRows')
  set _inputLinesSelectedRows (data: UserInputLinesGridInterface) {
    this.inputLinesSelectedRows = data
    if (this.tabKey === 'user_input') {
      console.log('--------> User Input Data Update: ', data)
      console.log('--------> Current Input Lines Data: ', this.inputLinesSelectedRows)
    }
  }

  // Track the current tab
  selectedIndex
  @Input('selectedIndex')
  set _selectedIndex (index) {
    console.log('---> Tab index changed: Grid Comp: ', index)

    if (this.tabKey !== 'user_input') {
      console.log('--------> Input Lines Selected Rows: ', this.inputLinesSelectedRows)
      if (
        !this.initialRender &&
                index?.currentRowSelection?.ids &&
                index?.currentRowSelection?.ids !== this.inputLinesSelectedRows?.ids
      ) {
        console.log('---> Clearing stored grid data: ', index)
        if (this.gridApi && !this.gridApi['destroyCalled']) {
          if (this.tabKey === 'carton_summary') {
            this.outputGridApi?.setGridOption('pinnedTopRowData', [])
          }
          this.gridApi?.setGridOption('columnDefs', [])
          this.gridApi.setGridOption('rowData', [])
          this.outputGridApi?.setGridOption('rowData', [])
          this.gridApi.flushAsyncTransactions()
          this.gridApi.expireValueCache()
          this.gridApi.showLoadingOverlay()
          this.selectedRowIndex = 0
        }
      }
      if (!this.initialRender) {
        console.log('Not initial render')
        this.checkAndSelectCorrectNode()
      }
    }
  }

  setGridState (gridName, gridOptionsParam) {
    // Only apply the persisted saved view once per grid - later calls happen after
    // data reloads (e.g. post-edit refreshes) and shouldn't clobber the user's live filters
    if (this.savedViewAppliedFor.has(gridName)) {
      return
    }
    // Apply the saved column state
    console.log('applying state for grid', gridName)
    console.log('this.storedGridSettings', this.storedGridSettings)
    if (Object.keys(this.storedGridSettings).length > 0 && this.storedGridSettings[gridName]) {
      const { colDefs, filters } = this.storedGridSettings[gridName]
      // Apply the saved column state
      gridOptionsParam?.applyColumnState({
        state: colDefs
      })
      // Apply the column order
      gridOptionsParam?.moveColumns(
        colDefs.map(col => col.colId),
        0
      )
      // Apply the saved filter state
      gridOptionsParam?.setFilterModel(filters)
      this.savedViewAppliedFor.add(gridName)
    }
  }

  // Update data for the output grids
  outputGridData
  @Input('outputGridData')
  set _outputGridData (updatedOutputGridData) {
    console.log('set output grid data')
    const { tabKey } = this.importTabData
    this.tabKey = tabKey
    if (this.tabKey !== 'user_input') {
      console.log('this.tabKey', this.tabKey)
      this.outputGridOptions.context.gridName = this.tabKey + '_detail'
      this.outputChartOptions = {
        ...DefaultChartOptions,
        plugins: {
          ...DefaultChartOptions.plugins,
          title: {
            display: true,
            position: 'top',
            text: this.tabKey === 'carton_summary' ? 'Total Carton Qty' : 'Reco vs Buy',
            font: {
              size: '16px'
            }
          },
          tooltip: {
            enabled: true,
            callbacks: {
              label: context => {
                switch (context.dataset.type) {
                  case 'line':
                    return (
                      ' ' + context.dataset.label + ': ' + (context.raw * 100).toFixed(2) + '%'
                    )
                  case 'bar':
                    console.log('context.dataset.label')
                    return ' ' + context.dataset.label + ': ' + context.raw
                  default:
                    return (
                      ' ' +
                                            context.raw +
                                            ' (' +
                                            (
                                              (100 * context.raw) /
                                                context.dataset.data.reduce((accumulator: number, item: number) => {
                                                  return accumulator + item
                                                }, 0)
                                            ).toFixed(2) +
                                            '%)'
                    )
                }
              }
            }
          }
        }
      }

      this.outputDetailChartOptions = {
        ...this.outputChartOptions,
        plugins: {
          ...this.outputChartOptions.plugins,
          tooltip: {
            enabled: true,
            callbacks: {
              label: context => {
                switch (context.dataset.type) {
                  case 'line':
                    return context.dataset.label + ': ' + (context.raw * 100).toFixed(2) + '%'
                  case 'pie':
                    return context.raw
                  default:
                    return (
                      context.dataset.label +
                                            ': ' +
                                            context.raw +
                                            (context.datasetIndex === 1
                                              ? ' (mismatch: ' +
                                                  (
                                                    context.chart.data.datasets[0].data[context.dataIndex] -
                                                      context.raw
                                                  ).toFixed(2) +
                                                  ')'
                                              : '')
                    )
                }
              }
            }
          }
        },
        scales: {
          y: {
            position: 'left',
            min: 0,
            max: scale => {
              return scale.chart.data.datasets.slice(0, 2).reduce((accumulator: number, item) => {
                const sum = item.data.reduce((a2: number, i2: number) => {
                  return a2 + i2
                }, 0)
                return sum > accumulator ? sum : accumulator
              }, 0)
            }
          },
          y2: {
            grid: {
              drawOnChartArea: false
            },
            position: 'right',
            min: 0,
            max: 1,
            ticks: {
              callback: (value, index, ticks) => {
                return (value * 100).toFixed(2) + '%'
              }
            }
          }
        }
      }
      if (!updatedOutputGridData) {
        // console.log('----> no output data')
        this.outputGridData = updatedOutputGridData
      } else if (
        (this.outputGridData && this.outputGridData?.buy_group_id !== updatedOutputGridData.buy_group_id) ||
                !this.outputGridData
      ) {
        console.log('--------> Output Grid Data Updated in Grid Comp: ', updatedOutputGridData)
        // Update the output data
        this.outputGridData = updatedOutputGridData
        this.configureOutputRowData(updatedOutputGridData[this.tabKey])
      }
    } else if (tabKey === 'user_input' && this.initialRender) {
      this.configureUserInputGridData()
    }

    this.customGridOptions.context.gridName = tabKey

    if (tabKey !== 'user_input') {
      Promise.all([this.generalAPIs.GetGridView(tabKey), this.generalAPIs.GetGridView(tabKey + '_detail')]).then(
        data => {
          this.storedGridSettings[tabKey] = data[0]?.is_success
            ? data[0].data
            : {
                colDefs: [],
                filters: []
              }
          this.storedGridSettings[tabKey + '_detail'] = data[1]?.is_success
            ? data[1].data
            : {
                colDefs: [],
                filters: []
              }
          this.setGridState(tabKey, this.gridApi)
          this.setGridState(tabKey + '_detail', this.outputGridApi)
        }
      )
    }
  }

  // ------------------------------------------------
  eventPointer: any // trigger context menu popup
  // AG Grid Settings
  gridApi: GridApi
  outputGridApi: GridApi
  columnApi: {}
  outputColumnApi: {}
  isAgGridLoading: boolean = false
  isOutputAgGridLoading: boolean = false
  gridOptions: any = {}
  columnDefs: any = []
  outputColumnDefs: any = []
  initialRender: boolean = true
  configuringUserData: boolean = false
  renderTreeDataGrid: boolean
  tabName: string = ''
  tabKey: string = ''

  dynamicSizes: string[] = []
  deliveryIDs: any[] = []
  selectedDeliveryID: number = null
  chartData: any = {}
  isUpdatingData: boolean = false
  currentUserInputRowSelection: any[] = []
  pendingRowSelected: boolean = false
  approvedRowSelected: boolean = false
  selectedGroupIDs: any[] = []
  selectedMasterRows: number = 0
  detailRowsSelected: any[] = []

  panelCollapsed = false

  // Enable and disable grid actions based on specific rules
  gridActionsEnabled = {
    viewData: false,
    exportBreaks: false,
    approve: false,
    markAsPendingReview: false,
    calculateBreaks: false,
    completed: false
  }

  // Saved grid settings
  storedGridSettings: any = {}
  filtersApplied: Record<string, boolean> = {}
  lastSavedDetailGridView: string
  // Track which grids have already had their saved filter/column state applied so
  // later data reloads (post-edit refreshes) don't clobber the user's live filter changes
  private readonly savedViewAppliedFor = new Set<string>()

  @Output() rowsSelectedEvent = new EventEmitter<any>()
  @Output() onViewDataEvent = new EventEmitter<any>()
  @Output() updateCanViewDataStatus = new EventEmitter<any>()
  @Output() updateSelectedRowIndex = new EventEmitter<any>()

  @ViewChild('confirmPopup') confirmPopup: ConfirmPopupComponent
  @ViewChild('confirmPopupDelete') confirmPopupDelete: ConfirmPopupComponent
  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger

  constructor (
    private readonly router: Router,
    private ref: DynamicDialogRef,
    public dialogService: DialogService,
    private readonly reviewOrdersAPIs: ReviewOrdersAPIs,
    public triggerApiCallsService: TriggerApiCallsService,
    private readonly generalAPIs: GeneralAPIs,
    private readonly sizeDistroDefaultParametersAPIs: SizeDistroDefaultParametersAPIs
  ) {
    this.routerSubscription = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.gridApi?.setFilterModel({})
      }
    })
    // Event is fired when the user right-clicks on an element to open the context menu
    document.addEventListener('contextmenu', this.onContextMenu)
  }

  private readonly onContextMenu = (event: MouseEvent) => {
    if (event.button === 2) {
      this.eventPointer = event
    }
  }

  ngOnInit () {
    const { field, tabKey, isTreeDataGrid } = this.importTabData
    this.tabName = field
    this.tabKey = tabKey
    this.renderTreeDataGrid = isTreeDataGrid
    this.configureGridOptions()
  }

  // On Grid Ready
  onGridReady (params?: GridReadyEvent) {
    console.log('AG Grid is Ready')
    if (params) {
      this.gridApi = params.api
      this.columnApi = params.api
    }
    this.isAgGridLoading = false
  }

  // On Output Bottom Grid Ready
  onOutputGridReady (params?: GridReadyEvent) {
    console.log('AG Grid is Ready')
    if (params) {
      this.outputGridApi = params.api
      this.outputColumnApi = params.api
    }
    this.isOutputAgGridLoading = false
  }

  configureGridOptions () {
    console.log('configuring grid options')
    // Shared Grid Configuations
    const mainOptions = {
      ...this.customGridOptions,
      ...RO_SharedGridOptions(this.tabKey),
      onRowSelected: null,
      onSelectionChanged: null,
      getContextMenuItems: (params: GetContextMenuItemsParams) => this.reviewOrdersGridContextMenu(params),
      isExternalFilterPresent: () => this.isExternalFilterPresent(),
      doesExternalFilterPass: node => this.doesExternalFilterPass(node),
      onFirstDataRendered: params => {
        AutoSizeColumns(params.api)
        if (this.tabKey !== 'user_input') {
          this.gridApi.getRenderedNodes()[0].setSelected(true)
        } else {
          this.setGridState('user_input', this.gridApi)
        }
      }
    }

    if (this.tabKey === 'user_input') {
      mainOptions['onRowSelected'] = async event => this.masterRowSelected(event)
      mainOptions['onRowGroupOpened'] = async event => this.onRowGroupOpened(event)
      // Advanced Filtering (Product Filters) -- only the Job List grid on
      // this tab has its own independent, filterable data-fetch
      mainOptions['sideBar'] = {
        toolPanels: [
          {
            id: 'roUserInputExternalFilters',
            labelDefault: 'Advanced Filtering',
            labelKey: 'roUserInputExternalFilters',
            iconKey: 'filter',
            toolPanel: ReviewOrdersUserInputToolPanelComponent
          }
        ],
        position: this.sideBarPanels.defaultLayout.position
        // No defaultToolPanel -- closed by default, only opens when the
        // user clicks the "Advanced Filtering" rail button.
      }
    } else {
      mainOptions['onSelectionChanged'] = async event => await this.rowSelectionChangedOutputMain(event)
    }

    // Set custom grid options for the tabs that should be configured with treedata
    if (this.renderTreeDataGrid) {
      console.log(
        'rendering tree data grid',
        TreeDataGridOptions(this.tabKey, this.importTabData?.treeDataParams)
      )
      this.gridOptions = {
        ...mainOptions,
        ...TreeDataGridOptions(this.tabKey, this.importTabData?.treeDataParams)
      }
      this.outputGridOptions = {
        ...this.outputGridOptions,
        groupRemoveSingleChildren: this.tabKey === 'carton_summary',
        ...TreeDataGridOptions(this.tabKey, this.importTabData?.treeDataParams),
        getRowStyle: (params: RowClassParams) => {
          if (this.tabKey === 'source_to_dest_summary' && params.node.level > 1) {
            return { fontWeight: 'normal !important' }
          }
          return params?.node?.data?.isAggRow || params.node.allLeafChildren?.length
            ? { fontWeight: 'bold' }
            : {}
        }
      }
    } else {
      this.gridOptions = mainOptions
    }
  }

  configureUserInputGridData () {
    console.log('---> Configuring User Input Grid Data')
    this.customGridOptions.context.gridName = 'user_input'
    if (!this.configuringUserData) {
      this.configuringUserData = true

      // Fetch the product tree for the Advanced Filtering panel independently --
      // it doesn't gate row data and the panel handles its own loading state.
      this.sizeDistroDefaultParametersAPIs
        .GetAllProducts({ queryString: '', selectedProd: null, selectedFilters: '' })
        .then(res => {
          this.productTreeData = res.data
          // The tool panel normally re-renders off the grid's own modelUpdated
          // event, but that's a separate, independent fetch (row data) racing
          // against this one -- if row data wins the race, modelUpdated fires
          // while productTreeData is still empty, and nothing re-triggers the
          // panel once this arrives afterward. Push it explicitly instead of
          // hoping modelUpdated happens to fire again later.
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
          const toolPanel = this.gridApi?.getToolPanelInstance('roUserInputExternalFilters') as
                        | ReviewOrdersUserInputToolPanelComponent
                        | undefined
          toolPanel?.updateExternalFilters(null)
        })
        .catch(err => console.error('---> Error fetching products', err))

      // The saved view has to resolve first -- it's what tells us which
      // product filters (if any) were selected last time, and those need to
      // be part of the very first row-data fetch, not applied after the fact.
      this.generalAPIs.GetGridView('user_input').then(gridViewRes => {
        this.storedGridSettings['user_input'] = gridViewRes?.is_success
          ? gridViewRes.data
          : {
              colDefs: [],
              filters: []
            }

        const savedProductFilters = this.storedGridSettings['user_input']?.externalFilters?.productFilters
        this.productFilters = Array.isArray(savedProductFilters) ? savedProductFilters : []

        this.fetchAndRenderUserInputRowData()
      })
    }
  }

  // Fetches the Job List row data (filtered by whatever's currently in
  // this.productFilters) and renders the grid -- used on initial load and
  // again whenever the Advanced Filtering panel's "Apply All" is clicked.
  async fetchAndRenderUserInputRowData () {
    const res = await this.reviewOrdersAPIs.GetAllReviewOrdersUserInputData(this.productFilters)
    if (res?.is_success) {
      console.log('detailgridinfo', this.importTabData.detailGridInfo)
      this.renderGrid({
        mainColDefs: this.importTabData.mainColDefs,
        detailColDefs: this.importTabData.detailGridInfo.columnDefs,
        rowData: res.data
      })
    } else {
      // Render the grid without row data
      this.renderGrid({
        mainColDefs: this.importTabData.mainColDefs,
        detailColDefs: this.importTabData.detailGridInfo.columnDefs,
        rowData: []
      })
    }
  }

  // Called by the Advanced Filtering tool panel's "Apply All" button
  async applyProductFilters () {
    this.gridApi.showLoadingOverlay()
    await this.fetchAndRenderUserInputRowData()
  }

  async configureOutputRowData (tabData) {
    const { tabKey, mainColDefs, detailColDefs, detailDynamicColDefDefault } = this.importTabData
    const rowData = tabData
    console.log(`---> Configuring ${tabKey} Grid Data`)

    try {
      // if (tabKey === 'store_size_breaks') {
      //   this.chartData = tabData?.chart_data
      // }
      // this.deliveryIDs = this.outputGridData[
      //   'additional_data'
      // ].delivery_ids.sort((a, b) => a - b)
      // this.selectedDeliveryID =
      //           this.inputLinesSelectedRows.selectedDeliveryID
      // this.dynamicSizes = this.outputGridData[tabKey][0].size_range
      //   ? this.outputGridData[tabKey][0].size_range.split('|')
      //   : []
      // console.log('this dynamic sizes', this.dynamicSizes)
    } finally {
      // Render grids
      switch (tabKey) {
        // Render standard grids
        case 'source_to_dest_summary':
        case 'carton_summary':
        case 'buy_summary':
          this.renderGrid({
            mainColDefs,
            detailColDefs,
            detailDynamicColDefs: this.tabKey === 'source_to_dest_summary' ? null : this.dynamicSizes,
            detailDynamicColDefDefault,
            rowData
          })
          break
        case 'store_size_breaks':
        case 'store_sku_plan':
          this.renderGrid({
            mainColDefs,
            mainDynamicColDefs: this.dynamicSizes,
            rowData
          })
          break
        default:
          console.error('no tab id!')
      }
    }
  }

  async renderGrid ({
    mainColDefs,
    detailColDefs = null,
    mainDynamicColDefs = null,
    detailDynamicColDefs = null,
    mainDynamicColDefDefault = {},
    detailDynamicColDefDefault = {},
    rowData = []
  }) {
    console.log(`---> Rendering ${this.tabKey} Grid Data `)
    const params = {
      tabKey: this.tabKey,
      packConfigs: this.packConfigs,
      mainColDefs,
      detailColDefs,
      mainDynamicColDefs,
      detailDynamicColDefs,
      mainDynamicColDefDefault,
      detailDynamicColDefDefault
    }

    const gridData = await OM_GenerateGridData(params)
    if (gridData) {
      console.log(`Review Orders - ${this.tabKey} col defs: `, gridData.mainColDefs)
      this.columnDefs = gridData.mainColDefs
      console.log('gridData', gridData)

      // Update the page title for exports
      this.gridOptions.context.pageTitle = `Review orders - ${ReverseFormatKey(this.tabKey)}`
      if (this.tabKey === 'user_input') {
        this.gridOptions.masterDetail = true
        this.detailCellRendererParams = {
          detailGridOptions: {
            ...CustomGridOptions,
            rowSelection: {
              mode: 'multiRow',
              enableClickSelection: true,
              checkboxes: false,
              headerCheckbox: false
            },
            // suppressRowClickSelection: true,
            enableRangeSelection: true,
            pagination: false,
            keepDetailRows: true,
            columnDefs: gridData.detailColDefs,
            defaultColDef: {
              flex: 1
            },
            columnTypes: CustomGridOptions.columnTypes,
            getRowId: (params: GetRowIdParams) => {
              return `rownode${params.data.id}`
            },
            onSelectionChanged: (event: SelectionChangedEvent) => this.rowSelectionChanged(event, true),
            getContextMenuItems: (params: GetContextMenuItemsParams) =>
              this.reviewOrdersGridContextMenu(params),
            context: {
              componentParent: this,
              openUserInputConfigSettings: (params: ICellRendererParams) =>
                this.openUserInputConfigSettings(params),
              gridName: 'user_input_detail'
            },
            // column events for saving state on api call
            onDisplayedColumnsChanged: (event: DisplayedColumnsChangedEvent) =>
              this.updateChangedDetailGridView(event),
            onFilterChanged: (event: FilterChangedEvent) => this.updateChangedDetailGridView(event),
            onSortChanged: (event: SortChangedEvent) => this.updateChangedDetailGridView(event)
          },
          getDetailRowData: (params: GetDetailRowDataParams) => {
            params.successCallback(params.data.detail_data)
          }
        }
        this.detailCellRendererParams['detailGridOptions']['rowSelection']['enableClickSelection'] = true
      } else {
        this.outputColumnDefs = gridData.detailColDefs
        console.log('output top grid col defs from om generate etc', gridData.detailColDefs)
        this.outputGridApi?.refreshCells()
      }
      // function to check if AgGrid is loading
      const checkAgGrid = () => {
        if (!this.isAgGridLoading) {
          if (this.gridApi && !this.gridApi['destroyCalled']) {
            // snapshot and restore filters on grid check
            const liveFilterModel =
                            this.tabKey === 'user_input' && this.savedViewAppliedFor.has(this.tabKey)
                              ? this.gridApi.getFilterModel()
                              : null

            this.gridApi?.setGridOption('columnDefs', gridData.mainColDefs)
            this.outputGridApi?.setGridOption('columnDefs', gridData.detailColDefs)
            console.log('setting row data', rowData)
            this.gridApi.setGridOption('rowData', rowData)
            if (this.tabKey == 'user_input') {
              // call AFTER setting data
              this.setGridState(this.tabKey, this.gridApi)
            }
            if (liveFilterModel && Object.keys(liveFilterModel).length) {
              this.gridApi.setFilterModel(liveFilterModel)
            }
            if (this.tabKey === 'user_input') {
              this.gridApi.refreshClientSideRowModel('group')
            } else {
              const selectedNodes = this.gridApi.getSelectedNodes()
              console.log('selectednodes', selectedNodes)
              if (selectedNodes.length) {
                console.log(
                  'setting bottom grid data',
                  JSON.stringify(selectedNodes[0].data.detailGridData)
                )
                this.outputGridApi.setGridOption('rowData', selectedNodes[0].data.detailGridData)
              } else {
                this.outputGridApi.setGridOption('rowData', [])
                this.checkAndSelectCorrectNode()
              }
            }
            this.gridApi.refreshClientSideRowModel('aggregate')
            if (this.tabKey !== 'user_input') {
              console.log('refreshing output data')
              this.outputGridApi.refreshClientSideRowModel()
              this.outputGridApi.refreshClientSideRowModel('group')
              this.outputGridApi.refreshClientSideRowModel('aggregate')
            }

            // if (
            //   this.tabKey !== 'user_input' &&
            //                 this.tabKey !== 'buy_summary'
            // ) {
            //   this.setDropdownValueForGrids({
            //     value: this.selectedDeliveryID
            //   })
            // }

            if (this.initialRender) {
              this.initialRender = false
              if (this.tabKey === 'user_input') {
                // Start the subscription and send the grid api to update the needed rows
                this.subscription?.unsubscribe()
                this.subscription = this.reviewOrdersAPIs.JobGridDataSubscription(
                  this.gridApi,
                  this.gridOptions.context.componentParent,
                  () => this.updateEnabledActions()
                )
              }
            }
            this.configuringUserData = false
          }
          clearInterval(setData)
        } else {
          console.log('Ag grid is loading')
        }
      }
      if (this.tabKey !== 'user_input') {
        this.setGridState(this.tabKey, this.gridApi)
        this.setGridState(this.tabKey + '_detail', this.outputGridApi)
      } // else don't call set grid state, checkaggrid will call it
      const setData = setInterval(checkAgGrid, 100)
    }
  }

  // Sets the context menu in the grid
  reviewOrdersGridContextMenu (params: GetContextMenuItemsParams) {
    console.log('---> context menu: ', params)
    console.log('---> this.gridActionsEnabled: ', this.gridActionsEnabled)
    const numRowsSelected = this.inputLinesSelectedRows.ids.length

    if (params.column) {
      // View Data Action
      const viewDataButton = [
        {
          name: 'View Data',
          action: event => this.onViewDataEvent.emit(event),
          disabled: !this.inputLinesSelectedRows.ids.length || !this.canViewData,
          cssClasses: ['text-accent']
        }
      ]
      // Export Breaks Action
      const exportBreaksButton = [
        {
          name: numRowsSelected > 0 ? 'Export Breaks' : 'Export Breaks (No Jobs Selected)',
          action: async () => await this.confirmPopup.confirm(this.eventPointer, 'Export Breaks'),
          disabled: !this.gridActionsEnabled.exportBreaks,
          cssClasses: ['text-warn']
        }
      ]
      const approveButton = [
        {
          name:
                        numRowsSelected > 0
                          ? `Approve Job${numRowsSelected === 1 ? '' : 's'}`
                          : 'Approve (No Jobs Selected)',
          action: () => this.onApprove('context-menu-action'),
          disabled: !this.gridActionsEnabled.approve,
          cssClasses: ['text-accent']
        }
      ]
      //  Mark as Pending Review Action
      const markAsPendingReviewButton = [
        {
          name:
                        numRowsSelected > 0
                          ? `Unapprove Job${numRowsSelected === 1 ? '' : 's'}`
                          : 'Unapprove (No Jobs Selected)',
          action: () => this.onMarkAsPendingReview('context-menu-action'),
          disabled: !this.gridActionsEnabled.completed || numRowsSelected === 0,
          cssClasses: ['text-accent']
        }
      ]
      // Calculate Breaks Action
      const calculateBreaksButton = [
        {
          name: numRowsSelected > 0 ? 'Calculate Breaks' : 'Calculate Breaks (No Jobs Selected)',
          action: () => this.onCalculateBreaks('context-menu-action'),
          disabled: !this.gridActionsEnabled.calculateBreaks,
          cssClasses: ['text-accent']
        }
      ]
      // Save View Action
      const saveViewButton =
                params.context.gridName === 'user_input_detail'
                  ? []
                  : [
                      {
                        name: 'Save Grid View',
                        action: async () => {
                          // if (params.context.gridName === 'user_input_detail') {
                          //   this.lastSavedDetailGridView = 'detail_' + params.node.data.buy_group_id + ' grid row id'
                          // }
                          await SaveGridState(this.gridApi, params, params?.context?.gridName, true)
                        },
                        cssClasses: ['text-accent']
                      },
                      'separator'
                    ]
      // Display the context menu items for the user input table only
      const getUserInputMenuItems = () =>
        this.tabKey === 'user_input'
          ? [
              ...approveButton,
              ...markAsPendingReviewButton,
              'separator',
              ...viewDataButton,
              ...calculateBreaksButton,
              ...exportBreaksButton,
              'separator',
              ...saveViewButton
            ]
          : [...saveViewButton]

      const result = [...getUserInputMenuItems(), ...DefaultGridContextMenu(params)]

      return result
    } else {
      return DefaultGridContextMenu(params)
    }
  }

  // On row change update emit the new data to the main Review Orders Component
  rowSelectionChanged (event: SelectionChangedEvent, wasDetailSelected: boolean) {
    // const masterRows = await event.api.getSelectedNodes()
    // masterRows.forEach(masterRow => {
    //   console.log('for each amster row', masterRow)
    //   masterRow.setExpanded(true)
    //   const detailGridInfo = this.gridApi.getDetailGridInfo('detail_' + masterRow.data.grid_row_id)
    //   if (detailGridInfo) {
    //     console.log('selecting all')
    //     detailGridInfo.api.selectAll()
    //   }
    // })

    // const detailRows = masterRows.reduce((accumulator, item) => {
    //   console.log('master row to reduce:', item)
    //   const copyRows = item.data.detail_data.map(row => {
    //     return { ...row }
    //   })
    //   copyRows.forEach(row => {
    //     row.buy_group_id = item.data.buy_group_id
    //   })
    //   return accumulator.concat(copyRows)
    // }, [])
    // console.log('all detail grid datas', detailRows)
    this.addToSelectedRows(event, wasDetailSelected)
  }

  // rowSelectionChangedDetail (event: SelectionChangedEvent) {
  //   // const stycIDs = []
  //   // let updatedGroupLevelData
  //   // let singleGroupsSelected
  //   // // Reset Grid Actions
  //   // this.gridActionsEnabled = {
  //   //   approve: false,
  //   //   markAsPendingReview: false,
  //   //   calculateBreaks: false
  //   // }
  //   // const selectedStatuses = {
  //   //   approved: false,
  //   //   pending_review: false,
  //   //   running: false
  //   // }
  //   // console.log('finding node by id')
  //   // console.log(event.api.getRowNode('rownode2'))

  //   // if (!this.configuringUserData) {
  //   //   try {
  //   //     const rows = event.api.getSelectedRows()
  //   //     console.log('---> User Input Row Selection Change: ', rows)

  //   //     this.currentUserInputRowSelection = rows
  //   //     // const groupsSelected = await
  //   //     singleGroupsSelected =
  //   //                 rows.length > 0
  //   //                   ? [...new Set([...rows.map(row => row.group)])]
  //   //                       .length === 1
  //   //                   : false
  //   //     updatedGroupLevelData = !singleGroupsSelected
  //   //       ? DefaultUserInputLinesGrid
  //   //       : {
  //   //           store_distro: rows[0]?.store_distro,
  //   //           selectedDeliveryID: rows[0]?.delivery_id,
  //   //           size_range: rows[0]?.size_range,
  //   //           isRowDisabled: false,
  //   //           channels: [],
  //   //           rows: [],
  //   //           row_ids: [],
  //   //           buy_group_id: rows[0]?.buy_group_id
  //   //         }

  //   //     const updateGridActionsAndData = rows.map((row, i) => {
  //   //       const status = FormatKey(row.status)
  //   //       // If only 1 group is selected -> update the top level group data
  //   //       // if (singleGroupsSelected) {
  //   //       //   const { row_id, channel, styc_id, grid_row_id } = row
  //   //       //   const { row_ids, channels } = updatedGroupLevelData

  //   //       //   if (!row_ids.includes(row_id)) {
  //   //       //     updatedGroupLevelData.row_ids.push(row_id)
  //   //       //   }
  //   //       //   if (!channels.includes(channel)) {
  //   //       //     updatedGroupLevelData.channels.push(channel)
  //   //       //   }
  //   //       //   if (!stycIDs.includes(styc_id)) {
  //   //       //     stycIDs.push(styc_id)
  //   //       //   }
  //   //       //   updatedGroupLevelData.rows.push([row_id, grid_row_id])
  //   //       // }

  //   //       // Update Grid functionality
  //   //       if (!selectedStatuses[status]) {
  //   //         selectedStatuses[status] = true
  //   //       }
  //   //       if (rows.length === i + 1) {
  //   //         const { approved, pending_review, running } =
  //   //                         selectedStatuses
  //   //         const editActionsEnabled = !running && rows.length > 0
  //   //         this.gridActionsEnabled = {
  //   //           approve: editActionsEnabled && !approved,
  //   //           markAsPendingReview:
  //   //                             editActionsEnabled && !pending_review,
  //   //           calculateBreaks: editActionsEnabled && !approved
  //   //         }
  //   //       }
  //   //       return row
  //   //     })
  //   //     if (updateGridActionsAndData && singleGroupsSelected) {
  //   //       if (updatedGroupLevelData.channels.length > 0) {
  //   //         updatedGroupLevelData.isRowDisabled =
  //   //                         stycIDs.length !== 1
  //   //       }
  //   //     }
  //   //   } finally {
  //   //     console.log(
  //   //       '----> Updating the row selection',
  //   //       updatedGroupLevelData
  //   //     )
  //   //     console.log('gridActions enabled: ', this.gridActionsEnabled)
  //   //     this.rowsSelectedEvent.emit({
  //   //       rowsSelected: singleGroupsSelected,
  //   //       data: updatedGroupLevelData
  //   //     })
  //   //   }
  //   // }
  //   this.addToSelectedRows()
  // }

  addToSelectedRows (event: SelectionChangedEvent, wasDetailSelected: boolean) {
    if (wasDetailSelected) {
      this.gridApi.forEachDetailGridInfo((info: DetailGridInfo) => {
        const parentNode = this.gridApi.getRowNode(info.id.slice(7))
        parentNode.setSelected(info.api.getSelectedNodes().length === parentNode.data.detail_data.length)
      })
    } else {
      this.gridApi.forEachDetailGridInfo((info: DetailGridInfo) => {
        const parentNode = this.gridApi.getRowNode(info.id.slice(7))
        if (parentNode.isSelected()) {
          info.api.selectAll()
        }
      })
    }
    const detailRows = this.updateEnabledActions()
    this.rowsSelectedEvent.emit({
      data: {
        rows: detailRows,
        ids: detailRows.map(row => row.id),
        wasDetailSelected
      }
    })
  }

  masterRowSelected (event: RowSelectedEvent) {
    console.log('master row selected', event)
    if (event.event || event.source === 'uiSelectAllFiltered') {
      if (!event.node.isSelected()) {
        console.log('row is deselected', event.node)
        this.gridApi.getDetailGridInfo('detail_' + event.node.id)?.api.deselectAll()
      }
      this.addToSelectedRows(null, false)
    } else {
      console.log('not a real event, ignore')
    }
  }

  // detailRowSelected (event: RowSelectedEvent) {
  //   console.log('detail row selected', event)
  //   if (event.source === 'uiSelectAll') {
  //     this.addToSelectedRows(null, true)
  //   }
  // }

  onRowGroupOpened (event: RowGroupOpenedEvent) {
    this.addToSelectedRows(null, false)
  }

  updateEnabledActions () {
    const masterRows = this.gridApi.getSelectedRows()
    let detailRows: any[] = masterRows.reduce((accumulator: any[], item: any) => {
      const copyRows = item.detail_data.map((row: any) => {
        return { ...row }
      })
      copyRows.forEach((row: any) => {
        row.buy_group_id = item.buy_group_id
      })
      return accumulator.concat(copyRows)
    }, [])
    this.selectedGroupIDs = []
    this.selectedMasterRows = masterRows.length
    this.gridApi.forEachDetailGridInfo(gridInfo => {
      if (!gridInfo.api) return
      detailRows = detailRows.concat(
        gridInfo.api.getSelectedNodes().map(node => {
          if (!this.selectedGroupIDs.includes(node.data.buy_group_id)) {
            this.selectedGroupIDs.push(node.data.buy_group_id)
          }
          return node.data
        })
      )
    })

    this.detailRowsSelected = detailRows

    console.log('this.selectedGroupIDs', this.selectedGroupIDs)
    console.log('this.selectedMasterRows', this.selectedMasterRows)
    console.log('masterRows[0]', masterRows[0])
    console.log('masterRows all', masterRows)
    console.log('detailRows all', detailRows)

    const selectedStatuses: Record<string, boolean> = {
      approved: false,
      completed: false,
      'in progress': false,
      failed: false
    }

    detailRows.forEach((row: any) => {
      if (row.status) selectedStatuses[row.status.toLowerCase()] = true
    })

    const { approved, completed, failed } = selectedStatuses
    const in_progress = selectedStatuses['in progress']

    if (
      !in_progress &&
            !failed &&
            ((this.selectedGroupIDs.length === 1 && this.selectedMasterRows === 0) ||
                (this.selectedGroupIDs.length === 0 && this.selectedMasterRows === 1) ||
                (this.selectedGroupIDs.length === 1 &&
                    this.selectedMasterRows === 1 &&
                    masterRows[0]?.buy_group_id === this.selectedGroupIDs[0]))
    ) {
      this.canViewData = true
      this.updateCanViewDataStatus.emit(true)
    } else {
      this.canViewData = false
      this.updateCanViewDataStatus.emit(false)
    }

    const editActionsEnabled = !in_progress && detailRows.length > 0
    this.gridActionsEnabled = {
      ...this.gridActionsEnabled,
      approve: editActionsEnabled && !approved,
      markAsPendingReview: editActionsEnabled && !completed,
      completed: editActionsEnabled && !completed,
      calculateBreaks: editActionsEnabled && !approved,
      exportBreaks: editActionsEnabled && approved && !completed && masterRows.length > 0
    }

    return detailRows
  }

  async rowSelectionChangedOutputMain (event: SelectionChangedEvent) {
    const nodes = event.api.getSelectedNodes()
    nodes.sort((a, b) => a.rowIndex - b.rowIndex)
    console.log('selected nodes', nodes)
    if (nodes.length > 0) {
      this.selectedRowIndex = nodes[0]?.rowIndex
      this.updateSelectedRowIndex.emit(nodes[0]?.rowIndex)
    }

    // get columns
    let dynamicSizesFiltered = []
    if (this.tabKey === 'buy_summary' || this.tabKey === 'carton_summary') {
      let dynamicSizes = []
      nodes.forEach(node => {
        console.log('finding sizes in node', node)
        dynamicSizes = dynamicSizes.concat(node.data.main_row_sizes)
      })
      console.log('after concat', dynamicSizes)
      dynamicSizesFiltered = dynamicSizes.reduce((accumulator, item) => {
        if (!accumulator.includes(item)) {
          accumulator.push(item)
        }
        return accumulator
      }, [])

      console.log('after reducing', dynamicSizesFiltered)

      dynamicSizesFiltered.sort((a, b) => {
        return a.size_order - b.size_order
      })

      console.log('after sorting', dynamicSizesFiltered)
    }
    this.dynamicSizes = dynamicSizesFiltered.map(size => size.product_size)
    const newDetailColDefs = await GenerateCols(this.importTabData.detailColDefs, this.tabKey)
    const newSizeColDefs = await GenerateDynamicCols(
      this.dynamicSizes,
      this.tabKey,
      this.importTabData.detailDynamicColDefDefault
    )

    // this.outputGridApi.setRowData([])

    if (this.tabKey === 'carton_summary') {
      this.outputGridApi.setGridOption('pinnedTopRowData', [])
    }
    this.outputGridApi.setGridOption('columnDefs', [])
    this.outputGridApi.setGridOption('columnDefs', newDetailColDefs.concat(newSizeColDefs))

    let newGridData = []
    if (nodes.length) {
      if (this.tabKey === 'carton_summary') {
        const totalsByStyc = {}
        nodes.forEach(node => {
          console.log('looking at node', node)
          const styc = node.data.styc_id
          console.log('found styc in nodes', styc)
          styc.split('/').forEach(s => {
            totalsByStyc[s] = {
              carton_id: ' ',
              pack_ttl: 0,
              isAggRow: false,
              styc_id: s
            }
          })
        })
        // make aggregate rows
        const totals = {
          carton_id: 'SKU Totals by Size',
          pack_ttl: 0,
          isAggRow: true,
          isExpanded: false
        }
        const totalsPct = {
          carton_id: 'SKU % by Size',
          pack_ttl: '--',
          isAggRow: true,
          isPctRow: true,
          isExpanded: false
        }
        const bulkTotals = {
          carton_id: 'In Bulk',
          pack_ttl: 0,
          isAggRow: true,
          isExpanded: false
        }
        const packTotals = {
          carton_id: 'In Pack',
          pack_ttl: 0,
          isAggRow: true,
          isExpanded: false
        }
        const profileTopRow = {
          carton_id: 'Profiles',
          pack_ttl: '--',
          isAggRow: true,
          isExpanded: false
        }

        const profileRows = {}
        this.dynamicSizes.forEach(size => {
          totals[size] = 0
          bulkTotals[size] = 0
          packTotals[size] = 0
          Object.keys(totalsByStyc).forEach(key => {
            totalsByStyc[key][size] = 0
          })
        })
        const totalsPctByStyc = cloneDeep(totalsByStyc)
        const bulksByStyc = cloneDeep(totalsByStyc)
        const packsByStyc = cloneDeep(totalsByStyc)
        nodes.forEach(node => {
          newGridData = newGridData.concat(node.data.detailGridData)
          const row = node.data.profile_row[0]
          row.pack_ttl = '--'
          row.carton_id = node.data.distro_id
          row.isAggRow = false
          row.isPctRow = true
          row.isPinnedRow = true
          if (!(node.data.distro_id in profileRows)) {
            profileRows[node.data.distro_id] = row
          }

          node.data.detailGridData.forEach(row => {
            this.dynamicSizes.forEach(size => {
              if (row[size] || row[size] == 0) {
                if (row.is_bulk || row.is_bulk_multiple) {
                  totals[size] += row[size]
                  bulkTotals[size] += row[size]
                  totals.pack_ttl += row[size]
                  bulkTotals.pack_ttl += row[size]
                  // by styc
                  totalsByStyc[row.styc_id][size] += row[size]
                  totalsByStyc[row.styc_id].pack_ttl += row[size]
                  bulksByStyc[row.styc_id][size] += row[size]
                  bulksByStyc[row.styc_id].pack_ttl += row[size]
                } else {
                  totals[size] += row.pack_ttl * row[size]
                  packTotals[size] += row.pack_ttl * row[size]
                  totals.pack_ttl += row.pack_ttl * row[size]
                  packTotals.pack_ttl += row.pack_ttl * row[size]
                  // by styc
                  // console.log('by styc', totalsByStyc)
                  // console.log('with styc', row.styc_id)
                  totalsByStyc[row.styc_id][size] += row.pack_ttl * row[size]
                  totalsByStyc[row.styc_id].pack_ttl += row.pack_ttl * row[size]
                  packsByStyc[row.styc_id][size] += row.pack_ttl * row[size]
                  packsByStyc[row.styc_id].pack_ttl += row.pack_ttl * row[size]
                }
              } else {
                console.log('size ' + size + 'not found in row, skipping', row)
              }
            })
          })
        })
        this.dynamicSizes.forEach(size => {
          totalsPct[size] = totals[size] / totals.pack_ttl
          Object.keys(totalsByStyc).forEach(styc_id => {
            totalsPctByStyc[styc_id][size] = totalsByStyc[styc_id][size] / totalsByStyc[styc_id].pack_ttl
            totalsPctByStyc[styc_id].isPctRow = true
            totalsPctByStyc[styc_id].isAggRow = false
            totalsPctByStyc[styc_id].pack_ttl = '--'
            // totalsPctByStyc[styc_id].isExpanded = (this.pinnedTopRowDataSections?.profileRows &&
            //   this.pinnedTopRowDataSections.profileRows[styc_id])
            //   ? this.pinnedTopRowDataSections.profileRows[styc_id].isExpanded
            //   : true
          })
          // bulkTotals[size + '|PCT'] = (100 * bulkTotals[size] / bulkTotals.pack_ttl).toFixed(2)
          // packTotals[size + '|PCT'] = (100 * packTotals[size] / packTotals.pack_ttl).toFixed(2)
          // if (isNaN(packTotals[size + '|PCT'])) {
          //   console.log('packtotals pct is nan', packTotals[size + '|PCT'])
          //   overallStore[size] = bulkTotals[size + '|PCT']
          // } else if (isNaN(bulkTotals[size + '|PCT'])) {
          //   console.log('bulktotals pct is nan', bulkTotals[size + '|PCT'])
          //   overallStore[size] = packTotals[size + '|PCT']
          // } else {
          //   console.log('neither is nan')
          //   console.log('bulktotals component', (bulkTotals.pack_ttl / totals.pack_ttl) * bulkTotals[size] / bulkTotals.pack_ttl)
          //   console.log('packtotals component', (packTotals.pack_ttl / totals.pack_ttl) * packTotals[size] / packTotals.pack_ttl)
          //   overallStore[size] = (((bulkTotals[size] / bulkTotals.pack_ttl) + (packTotals[size] / packTotals.pack_ttl)) * 50).toFixed(2) + '%' // 50 = 100 (for pct) / 2 (for average)
          // }
        })
        console.log(
          'profileRowsChildren',
          Object.keys(profileRows).map(key => profileRows[key])
        )
        this.bulkTotalsForChart = bulkTotals
        this.packTotalsForChart = packTotals
        this.pinnedTopRowDataSections = {
          totals: {
            topRow: totals,
            isExpanded: this.pinnedTopRowDataSections.totals
              ? this.pinnedTopRowDataSections.totals.isExpanded
              : false,
            children: Object.keys(totalsByStyc).map(key => totalsByStyc[key])
          },
          totalsPct: {
            topRow: totalsPct,
            isExpanded: this.pinnedTopRowDataSections.totalsPct
              ? this.pinnedTopRowDataSections.totalsPct.isExpanded
              : false,
            children: Object.keys(totalsPctByStyc).map(key => totalsPctByStyc[key])
          },
          bulkTotals: {
            topRow: bulkTotals,
            isExpanded: this.pinnedTopRowDataSections.bulkTotals
              ? this.pinnedTopRowDataSections.bulkTotals.isExpanded
              : false,
            children: Object.keys(bulksByStyc).map(key => bulksByStyc[key])
          },
          packTotals: {
            topRow: packTotals,
            isExpanded: this.pinnedTopRowDataSections.packTotals
              ? this.pinnedTopRowDataSections.packTotals.isExpanded
              : false,
            children: Object.keys(packsByStyc).map(key => packsByStyc[key])
          },
          profileRows: {
            topRow: profileTopRow,
            isExpanded: this.pinnedTopRowDataSections.profileRows
              ? this.pinnedTopRowDataSections.profileRows.isExpanded
              : false,
            children: Object.keys(profileRows).map(key => profileRows[key])
          }
        }

        // let orderTotalPcts = []
        // this.pinnedTopRowDataSections.totalsPct.children.forEach(child => {
        //   console.log('pushing child', child)
        //   orderTotalPcts.push(child)
        //   console.log('adding this.pinnedTopRowDataSections.profileRows[child.styc_id]', this.pinnedTopRowDataSections.profileRows[child.styc_id])
        //   console.log('from styc id', child.styc_id)
        //   orderTotalPcts = orderTotalPcts.concat(this.pinnedTopRowDataSections.profileRows[child.styc_id])
        // })
        // console.log('put pct children in pinnedtoprowdatasections')

        let pinnedTopRowData = []
        const keys = ['totals', 'totalsPct', 'bulkTotals', 'packTotals', 'profileRows']

        keys.forEach(key => {
          console.log('putting ' + key + ' top row in', this.pinnedTopRowDataSections[key].topRow)
          pinnedTopRowData = pinnedTopRowData.concat(this.pinnedTopRowDataSections[key].topRow)
          console.log('is expanded?', this.pinnedTopRowDataSections[key].isExpanded)
          if (this.pinnedTopRowDataSections[key].isExpanded) {
            console.log('yes, expanded, adding children', this.pinnedTopRowDataSections[key].children)
            this.pinnedTopRowDataSections[key].topRow.isExpanded = true
            pinnedTopRowData = pinnedTopRowData.concat(this.pinnedTopRowDataSections[key].children)
          }
        })

        this.pinnedTopRowData = pinnedTopRowData

        // this.pinnedTopRowData = [totals]

        // if (this.pinnedTopRowDataSections.totals.isExpanded) {
        //   this.pinnedTopRowData.concat(this.pinnedTopRowDataSections.totals.children)
        // }
        // this.pinnedTopRowData.concat(totalsPct)

        //   this.pinnedTopRowDataSections.totals.isExpanded ? (...this.pinnedTopRowDataSections.totals.children : ...[],
        //   totalsPct,
        //   // ...orderTotalPcts,
        //   bulkTotals,
        //   // ...this.pinnedTopRowDataSections.bulkTotals.children,
        //   packTotals,
        //   // ...this.pinnedTopRowDataSections.packTotals.children
        //   profileTopRow
        // ]
        console.log('pinnedtoprowdata', this.pinnedTopRowData)
      } else {
        nodes.forEach(node => {
          newGridData = newGridData.concat(node.data.detailGridData)
        })
        this.pinnedTopRowData = []
      }
      this.outputGridApi.setGridOption('rowData', newGridData)
    } else {
      newGridData = []
      this.pinnedTopRowData = []
      this.outputChartData = null
      this.outputDetailChartData = null
      console.log('newGridData', newGridData)
      this.outputGridApi.setGridOption('rowData', newGridData)
      return
    }

    if (this.tabKey === 'buy_summary') {
      const allNodes = []
      this.outputGridApi.forEachNode(node => {
        if (node.allLeafChildren?.length) {
          allNodes.push(node)
        }
      })
      // put together chart data
      // buy first
      const buyChartData = allNodes.map(row => {
        return {
          label: `${row.key} - Buy`, // key should be the channel name
          type: 'bar',
          data: this.dynamicSizes.map(size => row.aggData[FormatKey(size)]?.buy.toFixed(2))
        }
      })
      const targetChartData = allNodes.map(row => {
        return {
          label: `${row.key} - Reco`,
          type: 'bar',
          data: this.dynamicSizes.map(size => row.aggData[FormatKey(size)]?.target.toFixed(2))
        }
      })
      this.outputChartData = {
        datasets: buyChartData.concat(targetChartData),
        labels: this.dynamicSizes
      }
    } else if (this.tabKey === 'carton_summary') {
      let bulkTotal = 0
      let packTotal = 0
      this.dynamicSizes.forEach(size => {
        bulkTotal += this.bulkTotalsForChart[size]
        packTotal += this.packTotalsForChart[size]
      })
      console.log('bulkTotal', bulkTotal)
      console.log('packTotal', packTotal)
      this.outputChartData = {
        datasets: [
          {
            label: 'Total Carton Qty',
            data: [bulkTotal, packTotal]
          }
        ],
        labels: ['Bulk', 'Pack']
      }
    }
  }

  updateCartonSummaryPinnedRowExpansion () {
    console.log('updating carton summary pinned rows')
    let pinnedRowArray = [this.pinnedTopRowDataSections.totals.topRow]
    console.log('first top row', pinnedRowArray)
    if (this.pinnedTopRowDataSections.totals.isExpanded) {
      console.log('totals is expanded')
      pinnedRowArray = pinnedRowArray.concat(this.pinnedTopRowDataSections.totals.children)
    }
    pinnedRowArray.push(this.pinnedTopRowDataSections.totalsPct.topRow)
    if (this.pinnedTopRowDataSections.totalsPct.isExpanded) {
      console.log('sku pct by size is expanded')
      this.pinnedTopRowDataSections.totalsPct.children.forEach(child => {
        console.log('pushing child', child)
        console.log('with styc', child.styc_id)
        console.log('and carton id', child.carton_id)
        pinnedRowArray.push(child)
      })
    }
    pinnedRowArray.push(this.pinnedTopRowDataSections.bulkTotals.topRow)
    if (this.pinnedTopRowDataSections.bulkTotals.isExpanded) {
      pinnedRowArray = pinnedRowArray.concat(this.pinnedTopRowDataSections.bulkTotals.children)
    }
    pinnedRowArray.push(this.pinnedTopRowDataSections.packTotals.topRow)
    if (this.pinnedTopRowDataSections.packTotals.isExpanded) {
      pinnedRowArray = pinnedRowArray.concat(this.pinnedTopRowDataSections.packTotals.children)
    }
    pinnedRowArray.push(this.pinnedTopRowDataSections.profileRows.topRow)
    if (this.pinnedTopRowDataSections.profileRows.isExpanded) {
      pinnedRowArray = pinnedRowArray.concat(this.pinnedTopRowDataSections.profileRows.children)
    }
    this.pinnedTopRowData = pinnedRowArray
  }

  // Tab = Buy Summary (detail/store rows) -- committed OH/OO is real, submitted
  // per store, so it only ever shows for the specific store row that was clicked.
  openCommittedOhOoModal (params: ICellRendererParams) {
    console.log('---> opening committed oh/oo modal with event', params)
    const sizes: string[] = params.data.product_size || this.dynamicSizes

    const committed: Record<string, number> = {}
    sizes.forEach((size: string) => {
      const val = params.data[size]?.committed_oh_oo
      if (val !== null && val !== undefined) {
        committed[size] = val
      }
    })

    this.ref = this.dialogService.open(OhOoModalComponent, {
      showHeader: false,
      closeOnEscape: true,
      dismissableMask: false,
      styleClass: 'wide-short-modal',
      data: {
        title: 'Committed On Hand / On Order',
        sizes,
        committed: Object.keys(committed).length ? committed : null,
        uncommitted: null
      }
    })
  }

  // Tab = Buy Summary (main/job rows) -- uncommitted OH/OO has no store detail,
  // so it's shown once per job here rather than repeated on every store row.
  openUncommittedOhOoModal (params: ICellRendererParams) {
    console.log('---> opening uncommitted oh/oo modal with event', params)
    const sizes: string[] = params.data.size_range?.split('|') || this.dynamicSizes

    this.ref = this.dialogService.open(OhOoModalComponent, {
      showHeader: false,
      closeOnEscape: true,
      dismissableMask: false,
      styleClass: 'wide-short-modal',
      data: {
        title: 'Uncommitted On Hand / On Order',
        sizes,
        committed: null,
        uncommitted: params.data.uncommitted_oh_oo || null
      }
    })
  }

  openTargetBuyChart (params: ICellRendererParams) {
    // put together chart data
    // buy first
    console.log('opening target buy chart with event', params)
    const allNodes = []
    const buyChartData = [
      {
        label: `${params.data.location_id} - Buy`, // key should be the channel name
        type: 'bar',
        data: params.data.product_size.map((size: string) => params.data[size].buy),
        yAxisID: 'y'
      }
    ]
    const targetChartData = [
      {
        label: `${params.data.location_id} - Reco`, // key should be the channel name
        type: 'bar',
        data: params.data.product_size.map((size: string) => params.data[size].target.toFixed(2)),
        yAxisID: 'y'
      }
    ]
    const profileChartData = [
      {
        label: `${params.data.location_id} - Profile`, // key should be the channel name
        type: 'line',
        data: params.data.product_size.map((size: string) => params.data[size].profile_contrib),
        yAxisID: 'y2'
      }
    ]
    this.outputDetailChartData = {
      datasets: buyChartData.concat(targetChartData).concat(profileChartData),
      labels: this.dynamicSizes
    }
    this.dialogService.open(MismatchChartComponent, {
      showHeader: false,
      closeOnEscape: true,
      dismissableMask: false,
      styleClass: 'large-modal',
      data: {
        data: this.outputDetailChartData,
        options: this.outputDetailChartOptions
      }
    })
  }

  // Delivery Date Selection
  setDropdownValueForGrids (event) {
    console.log('---> Delivery ID Filter Updated: ', event.value)
    this.selectedDeliveryID = event.value
    this.gridOptions.api.onFilterChanged()
    const renderedNodes = this.gridApi.getRenderedNodes()
    if (renderedNodes && renderedNodes.length === 0) {
      this.gridApi.showNoRowsOverlay()
    } else {
      this.gridApi.hideOverlay()
    }
  }

  // Only apply external filters to tabs that display the Delivery ID Selection
  isExternalFilterPresent () {
    return false // this.tabKey !== 'user_input' && this.tabKey !== 'buy_summary'
  }

  // Function to update the rowData based on external filers (Delivery ID Selection)
  doesExternalFilterPass (node) {
    if (node.data) {
      return node.data.delivery_id === this.selectedDeliveryID
    }
  }

  // Tab = User Input
  openUserInputConfigSettings (cell) {
    console.log('---> cell: ', cell)
    const { id, channel, product_id } = cell.node.data
    // Api call -> GetParameterSettings
    this.ref = this.dialogService.open(UserInputConfigSettingsComponent, {
      showHeader: false,
      closeOnEscape: true,
      dismissableMask: false,
      styleClass: 'medium-modal',
      data: {
        id,
        channel,
        product_id
      }
    })

    // Modal Close Data
    this.ref.onClose.subscribe(data => {
      console.log('User Input Configs Modal Closed: ', data)
      // // Allow new api calls once the modal is closed
      this.triggerApiCallsService.onTriggerApiCalls({
        clear_api_calls: false
      })
      if (data) {
        this.gridApi.deselectAll()
        this.gridApi.showLoadingOverlay()
        this.configureUserInputGridData()
      }
    })
  }

  // Tab = Buy Summary
  openMismatchChartModal (cell) {
    console.log('---> mismatch modal cell', cell)
    const selectedRows = []
    const cellData = cell?.data || cell.node.aggData
    switch (cell.node.level) {
      case 0:
        // pass total
        selectedRows.push(cellData)
        break
      case 1:
        // pass this channel
        selectedRows.push(cellData)
        break
      case 2:
        // pass this channel and this store
        selectedRows.push(cellData)
        selectedRows.push(cell.node.parent.aggData)
        break
    }
    this.ref = this.dialogService.open(MismatchChartComponent, {
      showHeader: false,
      closeOnEscape: true,
      dismissableMask: false,
      styleClass: 'large-modal',
      data: {
        selectedRows,
        sizes: this.dynamicSizes,
        level: cell.node.level,
        metric: cellData?.metric
      }
    })
  }

  // Tab = Carton Summary
  openPackConfigModal (cell) {
    this.ref = this.dialogService.open(PackSummaryConfigComponent, {
      showHeader: false,
      closeOnEscape: true,
      dismissableMask: false,
      styleClass: 'large-modal',
      data: cell.node.data
    })
  }

  // Tab = Store Size Breaks
  viewTotalsCharts (cell) {
    const cellData = cell?.data || cell.node.aggData

    this.ref = this.dialogService.open(StoreTotalsChartsComponent, {
      showHeader: false,
      closeOnEscape: true,
      dismissableMask: false,
      styleClass: 'large-modal',
      data: {
        cellData,
        sizes: this.dynamicSizes,
        chartData: this.chartData,
        deliveryID: `delivery_id-${this.selectedDeliveryID}`,
        params: cell
      }
    })
  }

  async toggleUpdatingDataHandler (isUpdating: boolean, isSuccess?: boolean) {
    this.isUpdatingData = isUpdating

    if (isUpdating) {
      this.gridApi.showLoadingOverlay()
    } else if (!isUpdating && isSuccess) {
      try {
        await this.gridApi.flushAsyncTransactions()
        await this.gridApi.expireValueCache()
      } finally {
        this.gridApi.deselectAll()

        this.rowsSelectedEvent.emit({
          rowsSelected: null,
          data: DefaultUserInputLinesGrid
        })
        this.configureUserInputGridData()
      }
    } else {
      this.gridApi.hideOverlay()
    }
  }

  onDelete (params) {
    if (params.userInput === 'except') {
      if (params.command === 'Delete Buy Group') {
        this.toggleUpdatingDataHandler(true)
        Promise.all([
          this.saveInputGridState(),
          this.reviewOrdersAPIs.OnDeleteBuyGroup({
            buy_group_id: params.params.data.buy_group_id
          })
        ]).then(data => {
          this.gridApi.deselectAll()
          if (data[1]?.is_success) {
            this.toggleUpdatingDataHandler(false, true)
          } else {
            this.toggleUpdatingDataHandler(false)
          }
        })
      }
    }
  }

  // Approve Jobs
  onApprove ($event) {
    console.log('---> Approve Rows: ', this.inputLinesSelectedRows)
    this.toggleUpdatingDataHandler(true)
    const dataToSend = this.inputLinesSelectedRows.rows.map(rowData => rowData.id)
    Promise.all([this.saveInputGridState(), this.reviewOrdersAPIs.ApproveOrders({ job_ids: dataToSend })]).then(
      data => {
        if (data[1]?.is_success) {
          this.toggleUpdatingDataHandler(false, true)
        } else {
          this.toggleUpdatingDataHandler(false)
        }
      }
    )
  }

  // Mark Jobs as Pending Review
  onMarkAsPendingReview (event) {
    console.log('mark pending', this.inputLinesSelectedRows)
    this.toggleUpdatingDataHandler(true)

    const dataToSend = this.inputLinesSelectedRows.rows.map(rowData => rowData.id)

    Promise.all([
      this.saveInputGridState(),
      this.reviewOrdersAPIs.OnMarkAsPendingReview({
        job_ids: dataToSend
      })
    ]).then(data => {
      if (data[1]?.is_success) {
        this.toggleUpdatingDataHandler(false, true)
      } else {
        this.toggleUpdatingDataHandler(false)
      }
    })
  }

  // Calculate Breaks
  onCalculateBreaks (event) {
    // const dataToSend = this.currentUserInputRowSelection.map(rowData => {
    //   const { row_id, channel } = rowData
    //   return { row_id, channel }
    // })
    const dataToSend = this.inputLinesSelectedRows.rows.map(rowData => rowData.id)
    console.log('sending calc breaks row ids', this.inputLinesSelectedRows.rows)
    this.gridApi.showLoadingOverlay()

    Promise.all([this.saveInputGridState(), this.reviewOrdersAPIs.OnCalculateBreaks({ job_ids: dataToSend })]).then(
      data => {
        if (data[1]?.is_success) {
          dataToSend.forEach(job_name => {
            this.reviewOrdersAPIs.updateStatus(this.gridApi, job_name, 'In Progress', () =>
              this.updateEnabledActions()
            )
          })
          this.gridApi.deselectAll()
          this.gridApi.forEachDetailGridInfo((info: DetailGridInfo) => {
            info.api.deselectAll()
          })
          this.toggleUpdatingDataHandler(false, true)
        } else {
          this.toggleUpdatingDataHandler(false)
        }
      }
    )
  }

  // Export Job Data -> Exported data cannot be reversed
  onExportBreaks (event) {
    if (event.userInput === 'except') {
      this.toggleUpdatingDataHandler(true)
      const rows = this.gridApi.getSelectedRows()
      const dataToSend = rows.map(rowData => {
        return rowData.buy_group_id
      })

      console.log('---> Data to send on export: ', dataToSend)

      Promise.all([
        this.saveInputGridState(),
        this.reviewOrdersAPIs.OnExportBreaks({
          buy_group_ids: dataToSend
        })
      ]).then(data => {
        this.gridApi.deselectAll()
        if (data[1]?.is_success) {
          this.toggleUpdatingDataHandler(false, true)
        } else {
          this.toggleUpdatingDataHandler(false)
        }
      })
    }
  }

  onPanelOpened (event) {
    this.panelCollapsed = false
  }

  onPanelClosed (event) {
    this.panelCollapsed = true
  }

  checkAndSelectCorrectNode () {
    const selectedNodes = this.gridApi.getSelectedNodes()

    if (selectedNodes.length === 0 && !this.selectedRowIndex) {
      this.gridApi.forEachNode((node, index) => {
        if (index === 0) {
          node.setSelected(true)
        }
      })
    } else {
      this.gridApi.forEachNode((node, index) => {
        if (index === this.selectedRowIndex) {
          node.setSelected(true)
        } else if (index !== this.selectedRowIndex) {
          node.setSelected(false)
        }
      })
    }
  }

  async saveInputGridState () {
    return await Promise.all([
      SaveGridState(this.gridApi, this.gridOptions, 'user_input', false)
      // SaveGridStateWithData({ context: this.gridOptions.context }, this.storedGridSettings['user_input_detail'].colDefs, this.storedGridSettings['user_input_detail'].filters, 'user_input_detail', false)
    ])
  }

  updateChangedDetailGridView (event) {
    // this.storedGridSettings['user_input_detail'] = {
    //   colDefs: event.columnApi.getColumnState(),
    //   filters: event.api.getFilterModel()
    // }
    // this.lastSavedDetailGridView = 'detail_' + event.api.getRenderedNodes()[0].data.buy_group_id + ' grid row id'
  }

  @HostListener('unloaded')
  ngOnDestroy () {
    this.routerSubscription?.unsubscribe()
    this.subscription?.unsubscribe()
    document.removeEventListener('contextmenu', this.onContextMenu)
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
    if (this.gridApi) {
      this.gridApi.flushAsyncTransactions()
      this.gridApi.expireValueCache()
      this.gridApi = null
    }
  }
}
